import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  toast,
  Typography,
} from '@montugroup/design-system';
import { useMediaQuery, useTheme } from '@mui/material';
import type { CellContext, Row } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';

import type { StocktakeLines } from '@/hooks/stocktake/useSubmitStocktake';
import { useSubmitStocktake } from '@/hooks/stocktake/useSubmitStocktake';

import { StocktakeDiscrepancyBadge } from './StocktakeDiscrepancyBadge';
import { compareByName } from './stocktakeSort';
import { needsReviewRowSx, numericColumnMeta } from './stocktakeTableStyles';

export type SubmitStocktakeItem = Readonly<{
  productId: number;
  name: string;
  expectedStock: number;
  countedStock: number;
  discrepancy: number;
  reason?: string;
}>;

type SubmitStocktakeModalProps = Readonly<{
  open: boolean;
  items: readonly SubmitStocktakeItem[];
  onClose: () => void;
  onSubmitted: () => void;
}>;

type ItemCellProps = CellContext<SubmitStocktakeItem, unknown>;

const toStocktakeLines = (items: readonly SubmitStocktakeItem[]): StocktakeLines => {
  return Object.fromEntries(
    items.map((item) => [
      String(item.productId),
      { expectedQty: item.expectedStock, countedQty: item.countedStock, discrepancyReason: item.reason ?? null },
    ]),
  );
};

const needsReason = (item: SubmitStocktakeItem): boolean => {
  return item.discrepancy !== 0 && !item.reason;
};

// The design-system Table's default export is memoised, which erases its row type
const applyRowStyles = (row: Row<unknown>) => {
  return needsReason(row.original as SubmitStocktakeItem) ? needsReviewRowSx : {};
};

const ReasonCell = ({ row }: ItemCellProps) => {
  if (row.original.discrepancy === 0) {
    return null;
  }
  return (
    row.original.reason ?? (
      <Box component="span" sx={{ color: 'warning.dark' }}>
        No reason selected
      </Box>
    )
  );
};

const columns = [
  { accessorKey: 'name', header: 'Product', enableSorting: false },
  { accessorKey: 'expectedStock', header: 'Expected Stock', enableSorting: false, meta: numericColumnMeta },
  { accessorKey: 'countedStock', header: 'Counted Stock', enableSorting: false, meta: numericColumnMeta },
  {
    accessorKey: 'discrepancy',
    header: 'Discrepancy',
    enableSorting: false,
    meta: numericColumnMeta,
    cell: ({ row }: ItemCellProps) => <StocktakeDiscrepancyBadge discrepancy={row.original.discrepancy} />,
  },
  { accessorKey: 'reason', header: 'Reason for Adjustment', enableSorting: false, cell: ReasonCell },
];

export default function SubmitStocktakeModal({ open, items, onClose, onSubmitted }: SubmitStocktakeModalProps) {
  const { submitStocktake, isPending: isSubmitting } = useSubmitStocktake();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const onCancel = useCallback(() => {
    if (!isSubmitting) {
      onClose();
    }
  }, [onClose, isSubmitting]);

  const sortedItems = useMemo(() => [...items].sort(compareByName), [items]);
  const missingReasonCount = items.filter(needsReason).length;
  const canSubmit = items.length > 0 && missingReasonCount === 0;
  const matchedCount = items.filter((item) => item.discrepancy === 0).length;

  const onSubmit = () => {
    if (!canSubmit) {
      return;
    }
    submitStocktake(toStocktakeLines(items), {
      onSuccess: () => {
        setIsSubmitted(true);
        onClose();
        toast.success('Stocktake submitted');
      },
      onStockChanged: () => {
        onClose();
        toast.error('Stock levels changed since you counted. Review the expected stock and submit again.');
      },
      onError: () => toast.error('Failed to submit stocktake'),
    });
  };

  // Calling onSubmitted only after the exit transition stops the parent clearing the rows while they fade out
  const onExited = () => {
    if (isSubmitted) {
      setIsSubmitted(false);
      onSubmitted();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      fullWidth
      fullScreen={isSmallScreen}
      maxWidth="lg"
      slotProps={{ transition: { onExited } }}
    >
      <DialogTitle>Submit Stocktake</DialogTitle>
      <DialogContent>
        <Stack gap={6}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Submitting stock counts for the following items:
          </Typography>
          <Table
            data={sortedItems}
            columns={columns}
            pageSize={items.length}
            hasRowBackgroundColor={false}
            compact
            applyRowStyles={applyRowStyles}
          />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {matchedCount} product{matchedCount === 1 ? '' : 's'} had correct stock counts
          </Typography>
          {missingReasonCount > 0 && (
            <Typography variant="body2" sx={{ color: 'warning.dark' }}>
              Select a reason for {missingReasonCount} item{missingReasonCount > 1 ? 's' : ''} before submitting.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button variant="contained" loading={isSubmitting} disabled={!canSubmit} onClick={onSubmit}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}
